import { memo, useState, useCallback } from 'react';
import styled from 'styled-components';
import { Tooltip, Button, Modal } from 'antd';
import { ExpandOutlined, CompressOutlined, CloseOutlined, PlusOutlined } from '@ant-design/icons';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { availableFormsAtom, collectFormsPaginationAtom, selectedFormUUIDAtom } from '../../atoms/CollectFormAtoms';
import { currentProjectAtom } from '~/stores/ProjectStore';
import { CollectFormAPIs } from '~/common/typescript/apis/CollectFormAPIs';
import { FormBuilderScreen } from '~/screens/FormBuilderScreen/FormBuilderScreen';
import CollectFormGrid from './CollectFormGrid';

interface FormListPanelProps {
  onMaximize?: () => void;
  onClose?: () => void;
  isMaximized?: boolean;
}

const FormListPanel = ({
  onMaximize,
  onClose,
  isMaximized,
}: FormListPanelProps) => {
  const currentProject = useAtomValue(currentProjectAtom);
  const [availableForms, setAvailableForms] = useAtom(availableFormsAtom);
  const setPaginationInfo = useSetAtom(collectFormsPaginationAtom);
  const setSelectedFormUUID = useSetAtom(selectedFormUUIDAtom);
  const [builderModalOpen, setBuilderModalOpen] = useState(false);

  const handleFormSelect = (formUuid: string) => {
    setSelectedFormUUID(formUuid);
  };

  const handleOpenCreateForm = () => {
    setBuilderModalOpen(true);
  };

  const refreshProjectForms = useCallback(async () => {
    if (!currentProject?.id) return;
    try {
      const response = await CollectFormAPIs.remote_CollectForm_getPage({
        projectId: currentProject.id,
        page: 1,
        pageSize: 100,
      });
      setAvailableForms(response.results || []);
      if (response.pagination) {
        setPaginationInfo({
          page: response.pagination.page,
          pageSize: response.pagination.pageSize,
          pageCount: response.pagination.pageCount,
          total: response.pagination.total,
        });
      }
    } catch (err) {
      console.error('[FormListPanel] Failed to refresh forms:', err);
    }
  }, [currentProject?.id, setAvailableForms, setPaginationInfo]);

  return (
    <Container>
      <ControlRow>
        <LeftControls>
          <RecordCount>Chọn biểu mẫu để xem dữ liệu</RecordCount>
        </LeftControls>
        <ControlActions>
          <Button
            type="primary"
            size="small"
            icon={<PlusOutlined />}
            onClick={handleOpenCreateForm}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              borderRadius: 6,
              fontWeight: 500,
            }}
          >
            Tạo biểu mẫu
          </Button>
          {onMaximize && (
            <Tooltip title={isMaximized ? 'Thu nhỏ' : 'Phóng to'}>
              <Button
                type="text"
                size="small"
                icon={isMaximized ? <CompressOutlined /> : <ExpandOutlined />}
                onClick={onMaximize}
              />
            </Tooltip>
          )}
          {onClose && (
            <Tooltip title="Đóng">
              <Button type="text" size="small" icon={<CloseOutlined />} onClick={onClose} />
            </Tooltip>
          )}
        </ControlActions>
      </ControlRow>
      <CollectFormGrid
        forms={availableForms as any}
        onSelectForm={handleFormSelect}
        onGoToFormsTab={handleOpenCreateForm}
      />

      <Modal
        open={builderModalOpen}
        onCancel={() => setBuilderModalOpen(false)}
        footer={null}
        closable={false}
        centered
        width={760}
        styles={{
          content: {
            padding: 0,
            borderRadius: 10,
            overflow: 'hidden',
            border: '1px solid #e2e8f0',
            boxShadow: '0 8px 30px rgba(15, 23, 42, 0.14)',
          },
          body: { padding: 0 },
        }}
        destroyOnClose
      >
        {builderModalOpen && (
          <FormBuilderScreen
            mode="project_form"
            initialProjectId={currentProject?.id}
            isModal
            onCloseModal={() => setBuilderModalOpen(false)}
            onSavedForm={async () => {
              await refreshProjectForms();
              setBuilderModalOpen(false);
            }}
          />
        )}
      </Modal>
    </Container>
  );
};

export default memo(FormListPanel);

const Container = styled.div`
  padding: 6px;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const ControlRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  margin-bottom: 6px;
`;

const RecordCount = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: #374151;
`;

const LeftControls = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ControlActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;
